import { Injectable } from '@angular/core'
import { AngularFireDatabase } from 'angularfire2/database'


import { Observable, Observer } from 'rxjs/Rx'
import 'rxjs/add/operator/switchMap'

import { User, Location } from './user.model'
import { FacebookProviderData } from '../../auth/facebook-provider-data.model'

import { environment } from '../../../environments/environment'

import * as firebase from 'firebase'
import * as GeoFire from 'geofire'

@Injectable()
export class UserService {

  private readonly firebaseRef = firebase.database().ref(environment.firebaseRoot)
  private readonly storageRef = firebase.storage().ref(environment.firebaseRoot)
  private readonly geoFire = new GeoFire(this.firebaseRef.child('geofire'))


  constructor(private db: AngularFireDatabase) {
  }

  public getUser(uid: string, origin?: number[]): Observable<User> {
    const userRef = this.firebaseRef.child(`users/${uid}`)

    return this.db.object(userRef)
      .map((user: any) => this.mapToUser(user, origin))
      .publishReplay(1)
      .refCount()
  }

  public saveUser(user: User): Promise<any> {
    const item = {
      credits: user.credits,
      description: user.description,
      firstName: user.firstname,
      lastname: user.lastname,
      images: user.images,
      email: user.email,
      preferences: user.preferences
    }

    return this.firebaseRef.child(`users/${user.uid}`).update(item) as Promise<any>
  }

  public updateProfile(data: FacebookProviderData): Promise<any> {
    const uid = data.uid
    return this.createAccountIfNotExists(data)
      .then(() => {
        const updates = {}
        updates[`/users/${uid}/email`] = data.email
        updates[`/users/${uid}/firstName`] = data.firstname
        updates[`/users/${uid}/lastName`] = data.lastname
        updates[`/users/${uid}/sex`] = data.gender
        updates[`/users/${uid}/birthday`] = data.birthday
        updates[`/users/${uid}/preferences`] = {
          female: true,
          male: true,
          follow: true,
          radius: 101,
          maxAge: 100,
          minAge: 18,
        }

        return this.firebaseRef.update(updates)
      })
  }

  public updatePreferences(uid: string, preferences: any): Promise<any> {
    return this.firebaseRef.child(`users/${uid}/preferences`).update(preferences) as Promise<any>
  }

  public getNotificationCount(uid: string): Observable<number> {
    const notifyCountRef = this.firebaseRef.child(`users/${uid}/notifyCount`)
    return this.db.object(notifyCountRef)
      .map((snapshot: any) => {
        return snapshot.$value
      })
  }

  public resetNotificationCount(uid: string): Promise<any> {
    return this.firebaseRef.child(`users/${uid}/notifyCount`).set(0) as Promise<any>
  }

  public updateDescription(uid: string, value: string): Promise<any> {
    return this.firebaseRef.child(`users/${uid}/description`).set(value) as Promise<any>
  }

  public updateProfilePicture(uid: string, data: string, index: number) {
    if (!data) {
      return Promise.resolve()
    }

    const userAssetRef = this.storageRef.child(`userImages/${uid}/image_${index}`)
    return userAssetRef.putString(data, 'data_url')
      .then((snapshot: firebase.storage.UploadTaskSnapshot) => {

        const updates = {}
        updates[`users/${uid}/images/${index}`] = snapshot.downloadURL

        return this.firebaseRef.update(updates)
      })
  }

  public removeProfilePicture(uid: string, index: number) {
    const userAssetRef = this.storageRef.child(`userImages/${uid}/image_${index}`)

    const updates = {}
    updates[`users/${uid}/images/${index}`] = null

    const deletePicturePromise = userAssetRef.delete()
    const deleteEntryPromise = this.firebaseRef.update(updates)

    const promises = [
      deleteEntryPromise,
      deletePicturePromise,
    ]

    return Promise.all(promises)
  }

  public getUserLocation(uid: string): Promise<number[]> {
   return this.geoFire.get(uid)
  }

  public setLocation(uid: string, latitude: number, longitude: number): Promise<any> {
    return this.geoFire.set(uid, [latitude, longitude])
  }

  public getBlockedUsers(uid: string): Observable<User[]> {
    const ref = this.firebaseRef.child(`users/${uid}/blocked`)

    return this.db.list(ref)
      .map((result: any) => {

        // transform firebase result to array of uid
        return <string[]>result
          .filter((item: any) => item.$value === true)
          .map((item: any) => item.$key)
      })
      .switchMap((result: string[]) => {
        // Retrieve profile of blocked users
        const observableBatch = result.map((blockedUid: string) => this.getUser(blockedUid).first())
        if (observableBatch.length > 0) {
          return Observable.forkJoin(observableBatch)
        } else {
          return Observable.of([])
        }
      })
  }

  public blockUser(sourceUid: string, targetUid: string): Promise<any> {
    const updates = {}
    updates[`/users/${sourceUid}/blocked/${targetUid}`] = true
    updates[`/blockedUsers/${targetUid}/${sourceUid}`] = true

    return <Promise<any>>this.firebaseRef.child(targetUid).once('value')
      .then((snapshot: firebase.database.DataSnapshot) => {
        if (!snapshot.child('blocked').child(sourceUid).exists()) {
          updates[`/users/${targetUid}/blocked/${sourceUid}`] = false
        }

        return <Promise<any>>this.firebaseRef.update(updates)
      })
  }

  public unblockUser(sourceUid: string, targetUid: string): Promise<any> {
    const updates = {}
    updates[`/users/${sourceUid}/blocked/${targetUid}`] = null
    updates[`/blockedUsers/${targetUid}/${sourceUid}`] = null

    return <Promise<any>>this.firebaseRef.child(`users/${targetUid}`).once('value')
      .then((snapshot: firebase.database.DataSnapshot) => {
        if (snapshot.child('blocked').child(sourceUid).val() === false) {
          updates[`/users/${targetUid}/blocked/${sourceUid}`] = null
        }

        return <Promise<any>>this.firebaseRef.update(updates)
      })
  }

  private createAccountIfNotExists(data: FacebookProviderData): Promise<any> {
    const uid = data.uid

    return <Promise<any>>this.firebaseRef.child(`/users/${uid}`).once('value')
      .then((snapshot: firebase.database.DataSnapshot) => {
        if (!snapshot.exists()) {
          return this.firebaseRef.child(`users/${uid}`).set({
            firstName: data.firstname,
            lastName: data.lastname,
            email: data.email,
            sex: data.gender,
            birthday: data.birthday,
            images: [data.imageUrl],
            credits: 0,
            notifyCount: 0
          })
        } else {
          return Promise.resolve()
        }
      })
  }

  private mapToUser(item: any, origin?: number[]) {
    const user = new User()

    user.uid = item.$key
    user.email = item.email
    user.firstname = item.firstName
    user.lastname = item.lastName
    user.description = item.description
    user.birthday = item.birthday
    user.credits = item.credits
    user.images = item.images
    user.city = item.city
    user.country = item.country
    user.subExpirationDate = item.subExpiryDate

    if (item.preferences) {
      user.preferences = {
        male: item.preferences.male,
        female: item.preferences.female,
        minAge: item.preferences.minAge,
        maxAge: item.preferences.maxAge,
        radius: item.preferences.radius,
        follow: item.preferences.follow,
      }
    }

    user.location$ = Observable.fromPromise(this.geoFire.get(user.uid))
      .map((latLng: number[]) => {
        const location = new Location(latLng)

        if (origin) {
          location.distance = GeoFire.distance(origin, latLng)
        }

        return location
      })

    return user
  }
}



// WEBPACK FOOTER //
// C:/Users/florian.gadal/Workspace/cinenow-web/src/app/users/shared/user.service.ts